// sequentialCollector.js — Shared sequential multi-click collector with optional reorder +
// live mil-sym preview (drawrule-arrow-order ticket 01).
//
// Thin wrapper over createMultiPointCollector + startLivePreview. Dedicated per-DrawRule tools
// supply reorderOnFinish themselves — this module never branches on SIDC / DrawRule.
//
// Preview modes (mirrors patterns in directionalArrowTools / miscBespokeTools AXIS):
//   - tip-last + cursorFirst, applyReorderToPreview=false: provisional = [cursor, ...raw]
//     equals tip-to-front of [...raw, tip] — used by LINE9-style and LINE29/30 tip@P1 tools.
//   - full reverse + applyReorderToPreview=true + cursorFirst: provisional =
//     [cursor, ...reverse(raw)] — used by unlimited LINE1 DIRATK* polylines.
//   - identity + cursorFirst=false: provisional = [...raw, cursor].

import { createMultiPointCollector } from './multiPointCollector';
import { startLivePreview } from './livePreview';

const identity = (coords) => coords;

/**
 * @param {import('ol/Map').default} map
 * @param {object} opts
 * @param {number} opts.minCount
 * @param {number} [opts.maxCount=Infinity]
 * @param {(coords: [number,number][]) => void} opts.onFinish — receives coords AFTER reorderOnFinish
 * @param {(coords: [number,number][]) => [number,number][]} [opts.reorderOnFinish] — library order transform
 * @param {string|null} [opts.sidc]
 * @param {number} [opts.previewMinPoints] — default max(1, minCount - 1)
 * @param {boolean|(() => boolean)} [opts.cursorFirst=false]
 * @param {boolean} [opts.applyReorderToPreview=false] — apply reorderOnFinish to preview getCollected
 * @param {(index: number) => string} [opts.roleForIndex]
 * @param {Function} [opts.onSketchUpdate]
 * @param {Function} [opts.styleResolver]
 * @param {boolean} [opts.allowDblClickFinish]
 * @param {(n: number) => boolean} [opts.canFinish]
 * @param {() => void} [opts.onCleanup]
 */
export function createSequentialCollector(map, {
    minCount,
    maxCount = Infinity,
    onFinish,
    reorderOnFinish = identity,
    sidc = null,
    previewMinPoints = null,
    cursorFirst = false,
    applyReorderToPreview = false,
    roleForIndex,
    onSketchUpdate,
    styleResolver,
    allowDblClickFinish,
    canFinish,
    onCleanup: callerOnCleanup,
}) {
    let stopLive = null;

    const handle = createMultiPointCollector(map, {
        minCount,
        maxCount,
        onFinish: (coords) => onFinish(reorderOnFinish(coords)),
        roleForIndex,
        onSketchUpdate,
        styleResolver,
        allowDblClickFinish,
        canFinish,
        onCleanup: () => {
            if (stopLive) { stopLive(); stopLive = null; }
            callerOnCleanup?.();
        },
    });

    if (sidc) {
        const minPts = previewMinPoints ?? Math.max(1, minCount - 1);
        const getCollected = applyReorderToPreview
            ? () => reorderOnFinish(handle.getCollected())
            : handle.getCollected;
        stopLive = startLivePreview(map, sidc, getCollected, minPts, { cursorFirst });
    }

    return handle;
}

/** Last click → index 0; earlier clicks keep relative order. */
export function tipToFront(coords) {
    if (!coords.length) return coords;
    return [coords[coords.length - 1], ...coords.slice(0, -1)];
}

/** Full array reverse (LINE1 DIRATK* / 2-pt tip@P1). */
export function reverseAll(coords) {
    return [...coords].reverse();
}

/** Swap first two only (LINE10/LINE24 spine). */
export function reverseFirstTwo(coords) {
    if (coords.length < 2) return coords;
    return [coords[1], coords[0], ...coords.slice(2)];
}
